import { memo, useMemo } from "react"
import Config from "react-global-configuration"
import MuiAvatar from "@material-ui/core/Avatar"
import { AccountCircle } from "@material-ui/icons"
import { makeStyles, Badge } from "@material-ui/core"
import clsx from "clsx"
import dayjs from "dayjs"
import { getAvatarColorFromId, getAllStaffAvatarColor } from "@utils/avatarColors"
import { Tooltip } from "../Tooltip/Tooltip"
import { getImageSource } from "../ImagesViewer/DisplayImage"

const ONLINE_THRESHOLD_MINUTES = 15
const AWAY_THRESHOLD_MINUTES = 60

const useAvatarStyles = makeStyles((theme) => ({
  colorDefault: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.primary
  },
  img: {
    opacity: 1,
    animationName: ({ kiosk, device }) => (kiosk || device ? null : "fadeInOpacity"),
    animationIterationCount: 1,
    animationTimingFunction: "ease-in",
    animationDuration: "0.35s"
  }
}))

const useBadgeStyles = makeStyles((theme) => ({
  online: {
    "& .MuiBadge-badge": {
      backgroundColor: theme.palette.success.main,
      boxShadow: `0 0 0 2px ${theme.palette.background.default}`
    }
  },
  away: {
    "& .MuiBadge-badge": {
      backgroundColor: theme.palette.warning.main,
      boxShadow: `0 0 0 2px ${theme.palette.background.default}`
    }
  },
  offline: {
    "& .MuiBadge-badge": {
      backgroundColor: theme.palette.grey[400],
      boxShadow: `0 0 0 2px ${theme.palette.background.default}`
    }
  }
}))

/**
 * @typedef {Object} Avatar
 * @property {string} key
 * @property {string} contentType TODO(ando): elaborate types, seem to be an enum of sorts

 * @typedef {Object} AvatarProps
 * @property {string}               [className],
 * @property {Avatar}               [avatar],
 * @property {string}               [id],
 * @property {string}               [firstName],
 * @property {string}               [lastName],
 * @property {string}               [fullName],
 * @property {JSX.Element}          [icon],
 * @property {number}               [number],
 * @property {boolean}              [external],
 * @property {boolean}              [isAllStaff],
 * @property {string}               [additionalInfo],
 * @property {number}               [width = 80],
 * @property {number}               [height = 80],
// NOTE(ando): below this, they seem to just be destructured to prevent passing to ...rest
 * @property {any}                  [identityProvider],
 * @property {any}                  [usernameChange],
 * @property {any}                  [activeAt],
 * @property {any}                  [inactive],
 * @property {any}                  [isOperandioSupport],
 *
 */

/**
 * @type {React.FC<AvatarProps>}
 */
const AvatarInner = memo(function AvatarInner({
  className,
  avatar,
  id,
  firstName,
  lastName,
  fullName,
  identityProvider,
  usernameChange,
  activeAt,
  icon,
  number,
  inactive,
  external,
  isAllStaff,
  additionalInfo,
  width = 80,
  height = 80,
  isOperandioSupport,
  ...rest
}) {
  const { clientKiosk: kiosk, clientDevice: device } = Config.get()
  const { imageViewerBaseUri, imageViewerBucketName } = Config.get("uploader")
  const baseClasses = useAvatarStyles({ kiosk, device })

  // Determine avatar colors based on ID or if it's "All staff"
  const avatarColors = isAllStaff ? getAllStaffAvatarColor() : id ? getAvatarColorFromId(id) : null

  // Exclude colorDefault class when we have custom avatar colors to prevent style conflicts
  const classes = avatarColors ? { ...baseClasses, colorDefault: undefined } : baseClasses

  const externalTitle = external ? " (External)" : ""

  // Get merged style with avatar colors first
  const getMergedStyle = () => {
    if (!avatarColors) return rest.style
    const avatarStyle = { backgroundColor: avatarColors.background, color: avatarColors.text }
    return rest.style ? { ...rest.style, ...avatarStyle } : avatarStyle
  }

  const { style, publicPhoneNumber, roleTitle, ...restUnstyled } = rest

  const appendInfo = (base) => {
    if (!additionalInfo) return base

    if (typeof additionalInfo === "string") {
      return `${base} ${additionalInfo}`
    }

    return (
      <>
        {base} {additionalInfo}
      </>
    )
  }

  if (avatar && avatar.key) {
    return (
      <Tooltip
        title={appendInfo(fullName ? `${fullName}${externalTitle}` : `${firstName} ${lastName}${externalTitle}`)}
      >
        <MuiAvatar
          classes={classes}
          className={className}
          alt={fullName || `${firstName} ${lastName}`}
          src={getImageSource(imageViewerBaseUri, imageViewerBucketName, avatar, "cover", width, height)}
          {...rest}
        />
      </Tooltip>
    )
  }
  if (firstName && lastName) {
    return (
      <Tooltip title={appendInfo(`${firstName} ${lastName}${externalTitle}`)}>
        <MuiAvatar classes={classes} className={className} style={getMergedStyle()} {...restUnstyled}>
          {firstName.charAt(0).toUpperCase()}
          {lastName.charAt(0).toUpperCase()}
        </MuiAvatar>
      </Tooltip>
    )
  }
  if (fullName) {
    return (
      <Tooltip title={appendInfo(`${fullName}${externalTitle}`)}>
        <MuiAvatar classes={classes} className={className} style={getMergedStyle()} {...restUnstyled}>
          {fullName.charAt(0).toUpperCase()}
        </MuiAvatar>
      </Tooltip>
    )
  }
  if (icon) {
    return (
      <MuiAvatar classes={classes} className={className} style={getMergedStyle()} {...restUnstyled}>
        {icon}
      </MuiAvatar>
    )
  }
  if (number) {
    return (
      <MuiAvatar classes={classes} className={className} style={getMergedStyle()} {...restUnstyled}>
        {number}
      </MuiAvatar>
    )
  }
  return (
    <MuiAvatar classes={classes} className={className} style={getMergedStyle()} {...restUnstyled}>
      <AccountCircle />
    </MuiAvatar>
  )
})

const Avatar = ({ activeAt, showPresenceStatus = true, ...rest }) => {
  const classes = useBadgeStyles()

  const presenceStatus = useMemo(() => {
    if (!activeAt) return null
    const now = dayjs()
    const lastActiveInMinutes = now.diff(dayjs(activeAt), "minute")
    if (lastActiveInMinutes <= ONLINE_THRESHOLD_MINUTES) return "online"
    if (lastActiveInMinutes <= AWAY_THRESHOLD_MINUTES) return "away"
    return "offline"
  }, [activeAt])

  // show online status badge only if activeAt is present and we have a presence status
  if (showPresenceStatus && presenceStatus) {
    return (
      <Badge
        className={clsx({
          [classes.online]: presenceStatus === "online",
          [classes.away]: presenceStatus === "away",
          [classes.offline]: presenceStatus === "offline"
        })}
        overlap="circular"
        variant="dot"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        aria-label={`User is ${presenceStatus}`}
      >
        <AvatarInner {...rest} />
      </Badge>
    )
  }

  return <AvatarInner {...rest} />
}

export { Avatar }
